import React from 'react';
import classNames from 'classnames';
import { useLanguage } from '../../../contexts/LanguageContext';

export interface PaginationProps {
  currentPage: number;
  pageSize: number;
  total: number;
  onChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  displayTotal?: boolean;
  pageSizeOptions?: number[];
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
  displayTotal = false,
  pageSizeOptions = [10, 20, 30, 50],
  className,
}) => {
  const { isRTL } = useLanguage();
  const totalPages = Math.ceil(total / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, total);

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const showPages = 5;
    const halfShow = Math.floor(showPages / 2);

    if (totalPages <= showPages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - halfShow);
      let end = Math.min(totalPages, currentPage + halfShow);

      if (currentPage <= halfShow + 1) {
        end = showPages;
      } else if (currentPage >= totalPages - halfShow) {
        start = totalPages - showPages + 1;
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onChange?.(page);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    onPageSizeChange?.(newSize);
    // Reset to page 1 when page size changes
    onChange?.(1);
  };

  if (total === 0) return null;

  return (
    <div className={classNames('ecme-pagination', className)}>
      {displayTotal && (
        <span className="ecme-pagination-total">
          {isRTL
            ? `عرض ${startItem}–${endItem} من ${total}`
            : `Showing ${startItem}–${endItem} of ${total}`}
        </span>
      )}

      {onPageSizeChange && (
        <div className="ecme-pagination-size">
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="ecme-pagination-select"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {isRTL ? `${size} / صفحة` : `${size} / page`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="ecme-pagination-pages">
        <button
          type="button"
          className={classNames('ecme-pagination-btn', {
            disabled: currentPage === 1,
          })}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label={isRTL ? 'الصفحة السابقة' : 'Previous page'}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isRTL ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'}
            />
          </svg>
        </button>

        {getPageNumbers().map((page, index) =>
          typeof page === 'number' ? (
            <button
              key={index}
              type="button"
              className={classNames('ecme-pagination-btn', {
                active: page === currentPage,
              })}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ) : (
            <span key={index} className="ecme-pagination-ellipsis">
              {page}
            </span>
          )
        )}

        <button
          type="button"
          className={classNames('ecme-pagination-btn', {
            disabled: currentPage === totalPages,
          })}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label={isRTL ? 'الصفحة التالية' : 'Next page'}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={isRTL ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
